import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/BillingProvidersService';
import { BillingProvider, BillingProviderStatus } from '../types';

export const useBillingProvider = (billingProviderId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const billingProviderQuery = useQuery({
        queryKey: ['billing-provider', billingProviderId],
        queryFn: () => fetch(billingProviderId!),
        enabled: !!billingProviderId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: BillingProvider) => {
            const valuesWithStatus = {
                ...values,
                status: 'Enabled' as BillingProviderStatus
            };
            return add(valuesWithStatus);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: BillingProvider) => {
            if (!billingProviderId) throw new Error('billingProviderId is required parameter');
            const { createdByUsername: _createdByUsername, ...rest } = values;
            return update(billingProviderId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
            queryClient.invalidateQueries({ queryKey: ['billing-provider', billingProviderId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!billingProviderId) throw new Error('billingProviderId is required parameter');
            return deleteRecord(billingProviderId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
        }
    });

    return {
        record: billingProviderQuery.data,
        loading: billingProviderQuery.isLoading,
        fetchRecord: billingProviderQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};